import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory, useLocation, useParams } from 'react-router';
import { intersection } from 'lodash';
import useTranslation from '../../../hooks/useTranslations';
import { resetSearch, search, selectSearchResults, selectSearchStatus } from '../../../redux/modules/searchSlice';
import { SearchGlassIcon } from '../../misc/icons/Icons';
import Inputfield from '../../misc/Inputfield';
import { setDocumentTitle } from '../../routing/SetDocumentTitle';
import ContentWrapper from '../layouts/partials/ContentWrapper';
import SidebarWrapper from '../layouts/partials/SidebarWrapper';
import PrivateLayout from '../layouts/PrivateLayout';
import ObjectTile from './partials/tiles/ObjectTile';
import TileWrapper from './partials/TileWrapper';
import ChipFilterMultipleFields from '../../misc/tags/ChipFilterMultipleFields';
import { selectPropertiesByIds } from '../../../redux/modules/data/propertiesSlice';
import { selectCampaignsByIds } from '../../../redux/modules/data/campaignsSlice';

const SearchResultsScreen = () => {
	const t = useTranslation();

	const dispatch = useDispatch();
	const { searchString } = useParams();
	const [onlyFields, setOnlyFields] = useState({ properties: [], campaign_id: []});

	setDocumentTitle([t('general_search'), searchString]);

	useEffect(() => {
		dispatch(search({ searchString }));
		setOnlyFields({ properties: [], campaign_id: []});

		return () => {
			dispatch(resetSearch());
		};
	}, [searchString, dispatch]);

	const searchResults = useSelector((state) => selectSearchResults(state));
	const searchStatus = useSelector((state) => selectSearchStatus(state));
	const breadcrumb = [{ route: 'search', translationKey: 'general_search' }];

	const objInOnlyFields = (object) => {
		const validKeys = Object.keys(onlyFields).filter((key) => {
			if (Array.isArray(object[key])) {
				return intersection(onlyFields[key], object[key]).length;
			}

			return onlyFields[key].includes(object[key]);
		});

		return !!validKeys.length;
	};

	if (!searchString || searchString === '') {
		return (
			<PrivateLayout customTree={breadcrumb}>
				<NoSearchString customTree={breadcrumb} />
			</PrivateLayout>
		);
	}

	if (searchStatus === 'pending') {
		return (
			<PrivateLayout customTree={breadcrumb}>
				<SearchLoading searchString={searchString} customTree={breadcrumb} />
			</PrivateLayout>
		);
	}

	if (searchResults.length === 0) {
		return (
			<PrivateLayout customTree={breadcrumb}>
				<NothingFound searchString={searchString} customTree={breadcrumb} />
			</PrivateLayout>
		);
	}

	if (searchResults) {
		let title = `${t('ifu_search_results')} (${searchResults.length})`;
		if (searchResults.length === 100) {
			title = t('ifu_search_max_results').replace('#resultCount#', '100');
		}

		return (
			<PrivateLayout customTree={breadcrumb}>
				<SearchResultWrapper title={title} searchString={searchString} customTree={breadcrumb}>
					<>
						<ChipFilterMultipleFields
							objectList={searchResults}
							onSelectCallback={setOnlyFields}
							fieldMap={{
								properties: { fn: selectPropertiesByIds },
								campaign_id: { fn: selectCampaignsByIds, prefix: '#' },
							}}
						/>

						<TileWrapper containerClasses="pt-2">
							{searchResults && searchResults.map((object) => {
								if (onlyFields.properties.length || onlyFields.campaign_id.length) {
									if (!objInOnlyFields(object)) {
										return null;
									}
								}

								return <ObjectTile key={object.id} object={object} setIsModalOpen={false} />;
							})}
						</TileWrapper>
					</>
				</SearchResultWrapper>
			</PrivateLayout>
		);
	}

	return null;
};

export default SearchResultsScreen;

const SearchLoading = ({ searchString, customTree }) => {
	const t = useTranslation();

	// TODO: Show LoadingScreen?
	// TODO: Remove title?
	// TODO: Loading ist nur kurz, was dann Flackern der Texte verursacht... Kann man es verzögern um 1-2 Sekunden?
	return (
		<>
			<SearchResultWrapper title={t('general_loading')} searchString={searchString} customTree={customTree}>
				<></>
			</SearchResultWrapper>
		</>
	);
};

const NothingFound = ({ searchString, customTree }) => {
	const t = useTranslation();

	return (
		<>
			<SearchResultWrapper title={t('search_no_documents_found')} searchString={searchString} customTree={customTree}>
				<></>
			</SearchResultWrapper>
		</>
	);
};

const NoSearchString = ({ customTree }) => {
	const t = useTranslation();

	return (
		<>
			<SearchResultWrapper title={t('ifu_search_missing_parameters')} customTree={customTree}>
				<></>
			</SearchResultWrapper>
		</>
	);
};


const SearchResultWrapper = ({ children, title, searchString = '', customTree }) => {
	const t = useTranslation();
	const history = useHistory();
	const location = useLocation();

	return (
		<>
			<SidebarWrapper title={t('webapp_search_for')} hiddenMobileTitlePartTwo={searchString} customTree={customTree}>
				<div className="flex max-w-xs px-4 mb-6 -mx-5 -mt-3 lg:hidden">
					<Inputfield fieldName="mobileSearch" label={t('general_search')} icon={<SearchGlassIcon className="w-6 h-6" />} initialValue={searchString} handleChange={(newSearchString) => history.push(`/search/${newSearchString}`)} useDebounce autoFocus={location?.pathname?.includes('search') ?? false} />
				</div>
			</SidebarWrapper>
			<ContentWrapper title={title}>
				<>
					{children}
				</>
			</ContentWrapper>
		</>
	);
};
